import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Avatar,
  Badge,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  FiberManualRecord as OnlineIcon,
  CallEnd as EndChatIcon,
  Message as QuickReplyIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Assignment as FormIcon
} from '@mui/icons-material';
import { Stomp } from '@stomp/stompjs';
import axios from 'axios';
import Cookies from '../common/Cookies';
import { WS_URL, API_URL } from '../common/Constants';
import { useUserNotifications } from '../hooks/useUserNotifications';
import { useLocation } from 'react-router-dom';

const LiveChat = () => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  // Connection state
  const [connected, setConnected] = useState(false);
  const [username, setUsername] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Tab management
  const [activeTab, setActiveTab] = useState(0); // 0: Open, 1: My, 2: All

  // Chat management
  const [openChats, setOpenChats] = useState([]); // Un-assigned chats
  const [myChats, setMyChats] = useState([]); // Picked by current agent
  const [allChats, setAllChats] = useState([]); // All chats (completed)
  const [selectedChat, setSelectedChat] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]); // Messages for selected chat
  const [chatHistory, setChatHistory] = useState([]); // Pre-connection history for right panel
  const [messagesByChannel, setMessagesByChannel] = useState({}); // Maintain messages per channel: { channelName: [messages] }

  // Message input
  const [messageInput, setMessageInput] = useState('');

  // Quick reply selection
  const [selectedQuickReply, setSelectedQuickReply] = useState('');
  const [quickReplies, setQuickReplies] = useState([]); // Predefined messages from API

  // Unread messages tracking
  const [unreadChannels, setUnreadChannels] = useState(new Set()); // Track channels with unread messages

  // End chat confirmation dialog
  const [endChatDialogOpen, setEndChatDialogOpen] = useState(false);

  // Predefined messages management dialog
  const [predefinedMessagesDialogOpen, setPredefinedMessagesDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [formLabel, setFormLabel] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);


  const stompClientRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatHistoryEndRef = useRef(null); // Ref for chat history scroll
  const currentChannelRef = useRef(null); // Track current channel for message subscription
  const selectedChannelRef = useRef(null); // Track selected chat channel
  const subscriptionsRef = useRef({}); // Track subscriptions: { channelName: subscription }
  const joinedChannelsRef = useRef(new Set()); // Track channels we've sent JOIN for
  const reconnectTimeoutRef = useRef(null); // Track reconnection timeout
  const isReconnectingRef = useRef(false); // Track if reconnection is in progress
  const messageTextFieldRef = useRef(null); // Ref for message text field in predefined messages form
  const formPaperRef = useRef(null); // Ref for form Paper container

  // Add these new state variables for All tab (after line 61)
  const [userChatLogs, setUserChatLogs] = useState([]); // Chat logs for All tab
  const [selectedLog, setSelectedLogState] = useState(null); // Selected log in All tab
  const [sessionLogData, setSessionLogData] = useState(null); // Session log details
  const [agentMap, setAgentMap] = useState({}); // agentId -> agentName map
  const [loadingAllTab, setLoadingAllTab] = useState(false); // Loading state for All tab

  // User notification context
  const { openChatCount: contextOpenChatCount, refreshOpenChatCount, clearNewChatNotification } = useUserNotifications();
  const location = useLocation();

  // Helper to parse chatData (handles { messages: [...] } structure)
  const parseChatData = (chatData) => {
    if (!chatData) return [];
    try {
      const parsed = typeof chatData === 'string' ? JSON.parse(chatData) : chatData;
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.messages)) return parsed.messages;
      return [];
    } catch {
      return [];
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    // If it's already a formatted string (from toLocaleString or toLocaleTimeString), return as is
    if (typeof timestamp === 'string' && timestamp.includes(':')) {
      return timestamp;
    }

    // If it's a number (long value) or string number, convert it
    const dateValue = typeof timestamp === 'number' ? timestamp : parseInt(timestamp);
    if (!isNaN(dateValue)) {
      const date = new Date(dateValue);
      return date.toLocaleTimeString();
    }

    // Try parsing as ISO string
    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString();
      }
    } catch (e) {
      // Fallback
    }

    return timestamp.toString();
  };

  // Add formatDate helper function (after parseChatData function, around line 82)
  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const pad = n => n < 10 ? '0' + n : n;
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const yyyy = d.getFullYear();
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${MM}-${dd}-${yyyy} ${hh}:${mm}`;
  };

  // Add wrapper for selecting a log and fetching sessionLog (after formatDate)
  const setSelectedLog = async (log) => {
    setSelectedLogState(log);
    setSessionLogData(null);
    if (log && log.sessionId) {
      try {
        const res = await axios.post(
          `${API_URL}/search/sessionLog?sessionId=${encodeURIComponent(log.sessionId)}`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
            }
          }
        );
        setSessionLogData(res.data);
        console.log('Session log data:', res.data);
      } catch (err) {
        setSessionLogData(null);
        console.error('Failed to fetch session log:', err);
      }
    }
  };

  // Add fetchAgents function (after setSelectedLog)
  const fetchAgents = async () => {
    try {
      const res = await axios.post(`${API_URL}/agents/getAllAgents`, {}, {
        headers: {
          'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
        }
      });
      const agentsArr = res.data || [];
      // Build agentId -> agentName map
      const map = {};
      agentsArr.forEach(agent => {
        map[agent.id] = agent.agentName;
      });
      setAgentMap(map);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
      setAgentMap({});
    }
  };

  // Add fetchUserChatLogs function (after fetchAgents)
  const fetchUserChatLogs = async () => {
    setLoadingAllTab(true);
    try {
      const res = await axios.post(`${API_URL}/search/userChatLogs`, {}, {
        headers: {
          'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
        }
      });
      console.log('User chat logs fetched:', res.data);
      const logs = res.data || [];
      setUserChatLogs(logs);
      if (logs.length > 0) {
        setSelectedLog(logs[0]);
      } else {
        setSelectedLogState(null);
      }
    } catch (err) {
      console.error('Failed to fetch user chat logs:', err);
      setUserChatLogs([]);
      setSelectedLogState(null);
    } finally {
      setLoadingAllTab(false);
    }
  };

  // Fetch predefined messages from API
  const fetchPredefinedMessages = async () => {
    setLoadingMessages(true);
    try {
      const res = await axios.post(`${API_URL}/predefined-messages/getAll`, {}, {
        headers: {
          'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
        }
      });
      console.log('Predefined messages fetched:', res.data);
      const messages = res.data || [];
      setQuickReplies(messages);
    } catch (err) {
      console.error('Failed to fetch predefined messages:', err);
      setQuickReplies([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Handle open predefined messages dialog
  const handleOpenPredefinedMessagesDialog = () => {
    setPredefinedMessagesDialogOpen(true);
    fetchPredefinedMessages();
  };

  // Handle close predefined messages dialog
  const handleClosePredefinedMessagesDialog = () => {
    setPredefinedMessagesDialogOpen(false);
    setIsCreating(false);
    setEditingMessage(null);
    setFormLabel('');
    setFormMessage('');
  };

  // Handle create button click
  const handleCreateClick = () => {
    setIsCreating(true);
    setEditingMessage(null);
    setFormLabel('');
    setFormMessage('');
  };

  // Handle edit button click
  const handleEditClick = (message) => {
    setEditingMessage(message);
    setIsCreating(false);
    setFormLabel(message.label || '');
    setFormMessage(message.message || '');

    // Scroll to form and focus message field after state updates
    setTimeout(() => {
      if (formPaperRef.current) {
        formPaperRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (messageTextFieldRef.current) {
        messageTextFieldRef.current.focus();
      }
    }, 100);
  };

  // Handle cancel form
  const handleCancelForm = () => {
    setIsCreating(false);
    setEditingMessage(null);
    setFormLabel('');
    setFormMessage('');
  };

  // Handle submit form (create or update)
  const handleSubmitForm = async () => {
    if (!formLabel.trim() || !formMessage.trim()) {
      setError('Label and Message are required');
      return;
    }

    try {
      const messageData = {
        label: formLabel.trim(),
        message: formMessage.trim()
      };

      if (editingMessage) {
        // Update existing message
        messageData.id = editingMessage.id;
        const res = await axios.post(`${API_URL}/predefined-messages/update`, messageData, {
          headers: {
            'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
          }
        });
        console.log('Predefined message updated:', res.data);
      } else {
        // Create new message
        const res = await axios.post(`${API_URL}/predefined-messages/create`, messageData, {
          headers: {
            'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
          }
        });
        console.log('Predefined message created:', res.data);
      }

      // Refresh the list
      await fetchPredefinedMessages();

      // Reset form
      handleCancelForm();
      setError('');
    } catch (err) {
      console.error('Failed to save predefined message:', err);
      setError(err.response?.data?.error || 'Failed to save predefined message');
    }
  };

  // Handle delete button click
  const handleDeleteClick = (message) => {
    setMessageToDelete(message);
    setDeleteConfirmOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!messageToDelete) return;

    try {
      await axios.post(`${API_URL}/predefined-messages/delete`, {}, {
        params: { id: messageToDelete.id },
        headers: {
          'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
        }
      });
      console.log('Predefined message deleted:', messageToDelete.id);

      // Refresh the list
      await fetchPredefinedMessages();

      setDeleteConfirmOpen(false);
      setMessageToDelete(null);
      setError('');
    } catch (err) {
      console.error('Failed to delete predefined message:', err);
      setError(err.response?.data?.error || 'Failed to delete predefined message');
      setDeleteConfirmOpen(false);
      setMessageToDelete(null);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setMessageToDelete(null);
  };

  // Play sent/rec sound notification
  const playSound = (type) => {
    const soundFile = type === 'sent'
      ? '/sounds/sent_default.wav'
      : '/sounds/rec_default.wav';
    try {
      const audio = new Audio(soundFile);
      audio.play().catch(() => { });
    } catch (e) { }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages]);

  // Auto-scroll chat history to bottom when chatHistory changes
  useEffect(() => {
    chatHistoryEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Scroll to form and focus message field when editing
  useEffect(() => {
    if (editingMessage && formPaperRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        formPaperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Focus on message field after scrolling
        setTimeout(() => {
          messageTextFieldRef.current?.focus();
        }, 300);
      }, 100);
    }
  }, [editingMessage]);

  // Fetch unassigned chats from API
  const fetchUnassignedChats = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/live/getAllUnassignedChats`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
          }
        }
      );

      console.log('Unassigned chats fetched:', res.data);

      // Map ChannelDTO to chat structure
      const mappedChats = (res.data || []).map((channelDTO, index) => {
        // Extract channel name - if available from channelMessage, use that, else use channel field
        const channelName = channelDTO.channel || `channel-${index}`;
        const lastMessage = channelDTO.channelMessage?.content || '';
        const timestamp = channelDTO.channelMessage?.dateCreated
          ? new Date(channelDTO.channelMessage.dateCreated).toLocaleString()
          : new Date().toLocaleString();

        // Parse chatHistory if available
        const parsedHistory = parseChatData(channelDTO.chatHistory);

        return {
          id: channelName,
          visitorName: channelDTO.user?.name || channelDTO.user?.first_name || 'Visitor',
          lastMessage: lastMessage || 'No messages yet',
          timestamp: timestamp,
          isOnline: channelDTO.online || false, // Use the online field from server
          channel: channelName,
          history: parsedHistory, // Parsed chat history
          hasUser: channelDTO.user != null,
          tenantId: channelDTO.tenantId ? String(channelDTO.tenantId) : '',
          agentId: channelDTO.agentId ? String(channelDTO.agentId) : '',
          agentName: channelDTO.agentName || '',
          liveAssistantName: channelDTO.liveAssistantName || ''
        };
      });

      // Separate unassigned and assigned chats
      // If user is null, it's unassigned (Open tab)
      // If user is not null, it's assigned to logged-in user (My tab)
      const unassigned = mappedChats.filter(c => !c.hasUser);
      const assigned = mappedChats.filter(c => c.hasUser);

      setOpenChats(unassigned);
      setMyChats(assigned);

      // Update notification context with actual count
      refreshOpenChatCount(unassigned.length);

      console.log('Unassigned chats:', unassigned);
      console.log('Assigned chats (My):', assigned);
    } catch (err) {
      console.error('Failed to fetch unassigned chats:', err);
      setError('Failed to load unassigned chats');
      setOpenChats([]);
      setMyChats([]);
    } finally {
      setLoading(false);
    }
  };

  // Load unassigned chats on component mount
  useEffect(() => {
    fetchUnassignedChats();
    fetchPredefinedMessages();
    // Clear notification when LiveChat component is active
    clearNewChatNotification();

    // Check if we should open the Open tab (e.g., from notification click)
    if (location.state && location.state.openTab !== undefined) {
      setActiveTab(location.state.openTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearNewChatNotification, location.state]);

  // Listen for notification context updates and refresh chats
  useEffect(() => {
    // When context count increases, refresh the list
    if (contextOpenChatCount > 0) {
      console.log('New chat notification received, refreshing chat list...');
      fetchUnassignedChats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextOpenChatCount]);

  // Monitor connection health and attempt reconnection
  useEffect(() => {
    const connectionCheckInterval = setInterval(() => {
      if (myChats.length > 0 || selectedChat) {
        // Only check if user has active chats
        if (!stompClientRef.current || !stompClientRef.current.connected) {
          console.log('Connection lost detected, attempting reconnection...');
          if (!isReconnectingRef.current) {
            initializeConnection().catch(err => {
              console.error('Background reconnection failed:', err);
            });
          }
        }
      }
    }, 10000); // Check every 10 seconds

    return () => {
      clearInterval(connectionCheckInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myChats, selectedChat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize WebSocket connection (persistent with auto-reconnect)
  const initializeConnection = () => {
    console.log('----------------------------------------- Initializing connection -----------------------------------------');
    return new Promise((resolve, reject) => {
      if (stompClientRef.current && stompClientRef.current.connected) {
        console.log('Already connected, reusing existing connection');
        resolve(stompClientRef.current);
        return;
      }

      try {
        const socket = new WebSocket(WS_URL + '/ws/stomp-direct');

        const stompClient = Stomp.over(socket, {
          debug: (str) => console.log('STOMP Debug:', str),
          reconnectDelay: 5000,
          heartbeatIncoming: 4000,
          heartbeatOutgoing: 4000
        });

        // Connection success handler
        stompClient.connect({}, (frame) => {
          console.log('Connected to STOMP:', frame);
          setConnected(true);
          setError(''); // Clear any previous errors
          isReconnectingRef.current = false;

          stompClientRef.current = stompClient;

          // Resubscribe to all previously joined channels after reconnection
          const channelsToResubscribe = Array.from(joinedChannelsRef.current);
          if (channelsToResubscribe.length > 0) {
            console.log('Resubscribing to channels:', channelsToResubscribe);
            channelsToResubscribe.forEach(channel => {
              if (!subscriptionsRef.current[channel]) {
                const chat = myChats.find(c => c.channel === channel);
                if (chat) {
                  subscribeToChannel(channel, chat);
                }
              }
            });
          }

          resolve(stompClient);
        },
          // Connection error/disconnect handler
          (error) => {
            console.error('STOMP connection error/disconnect:', error);
            setConnected(false);
            stompClientRef.current = null;

            // Attempt automatic reconnection
            if (!isReconnectingRef.current) {
              isReconnectingRef.current = true;
              setError('Connection lost. Reconnecting...');

              // Clear existing timeout
              if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
              }

              // Attempt reconnection after 3 seconds
              reconnectTimeoutRef.current = setTimeout(() => {
                console.log('Attempting to reconnect...');
                initializeConnection()
                  .then(() => {
                    console.log('Reconnection successful');
                    setError('');
                  })
                  .catch((err) => {
                    console.error('Reconnection failed:', err);
                    setError('Failed to reconnect. Please refresh the page.');
                  });
              }, 3000);
            }

            reject(error);
          });
      } catch (err) {
        console.error('Connection error:', err);
        setError('Failed to initialize connection.');
        reject(err);
      }
    });
  };

  // Subscribe to a channel (if not already subscribed)
  const subscribeToChannel = async (channel, chat = null) => {
    // If already subscribed, return early (no need to send JOIN again)
    if (subscriptionsRef.current[channel]) {
      console.log('Already subscribed to channel:', channel, '- skipping JOIN');
      return;
    }

    // Check if we've already sent JOIN for this channel
    if (joinedChannelsRef.current.has(channel)) {
      console.log('Already joined channel:', channel, '- skipping JOIN');
      // Still subscribe to messages if not already subscribed
      if (!subscriptionsRef.current[channel]) {
        try {
          const stompClient = await initializeConnection();
          // Capture channel in closure to ensure message filtering
          const subscriptionChannel = channel;
          const subscription = stompClient.subscribe(`/topic/channel/${subscriptionChannel}`, (message) => {
            console.log('Received message on subscription for channel', subscriptionChannel, ':', message.body);
            const parsedMessage = JSON.parse(message.body);


            // CRITICAL: Only process messages that match this subscription's channel
            const messageChannel = parsedMessage.channel;
            if (messageChannel && messageChannel !== subscriptionChannel) {
              console.log('Ignoring message - channel mismatch. Expected:', subscriptionChannel, 'Got:', messageChannel);
              return; // Don't process messages for other channels
            }

            const messageObj = {
              ...parsedMessage,
              id: parsedMessage.id || Date.now() + Math.random(),
              timestamp: formatTimestamp(parsedMessage.timestamp) || new Date().toLocaleTimeString()
            };

            // Add to channel's message log
            addMessageToChannel(subscriptionChannel, messageObj);

            // Update active messages if this is the selected chat's channel
            setActiveMessages(prev => {
              const isForSelectedChat = selectedChannelRef.current === subscriptionChannel;
              if (!isForSelectedChat) return prev;

              // Only add if not already in list
              const exists = prev.find(m =>
                m.id === messageObj.id ||
                (m.content === messageObj.content &&
                  m.sender === messageObj.sender &&
                  m.timestamp === messageObj.timestamp)
              );
              if (exists) return prev;
              return [...prev, messageObj];
            });

            // Update chat card with last user message
            updateChatLastMessage(subscriptionChannel, messageObj);
            // Play rec sound if message is not from this agent
            if (messageObj.sender !== username) {
              playSound('rec');
            }
          });
          subscriptionsRef.current[channel] = subscription;
        } catch (err) {
          console.error('Failed to subscribe to channel:', err);
        }
      }
      return;
    }

    try {
      const stompClient = await initializeConnection();

      // Determine sender name - use chat object if provided, otherwise lookup
      const getSenderForChannel = (ch) => {
        if (chat && chat.liveAssistantName) {
          return chat.liveAssistantName;
        }
        if (selectedChat && selectedChat.channel === ch) {
          return selectedChat.liveAssistantName || username || 'Agent';
        }
        const fromMy = myChats.find(c => c.channel === ch);
        if (fromMy && fromMy.liveAssistantName) return fromMy.liveAssistantName;
        const fromOpen = openChats.find(c => c.channel === ch);
        if (fromOpen && fromOpen.liveAssistantName) return fromOpen.liveAssistantName;
        return username || 'Agent';
      };

      // Create message handler for this channel
      // Capture channel in closure to ensure message filtering
      const subscriptionChannel = channel;
      const subscription = stompClient.subscribe(`/topic/channel/${subscriptionChannel}`, (message) => {
        console.log('Received message on subscription for channel', subscriptionChannel, ':', message.body);
        const parsedMessage = JSON.parse(message.body);

        // CRITICAL: Only process messages that match this subscription's channel
        const messageChannel = parsedMessage.channel;
        if (messageChannel && messageChannel !== subscriptionChannel) {
          console.log('Ignoring message - channel mismatch. Expected:', subscriptionChannel, 'Got:', messageChannel);
          return; // Don't process messages for other channels
        }

        const messageObj = {
          ...parsedMessage,
          id: parsedMessage.id || Date.now() + Math.random(),
          timestamp: formatTimestamp(parsedMessage.timestamp) || new Date().toLocaleTimeString()
        };

        // Add to channel's message log
        addMessageToChannel(subscriptionChannel, messageObj);

        // Update active messages if this is the selected chat's channel
        setActiveMessages(prev => {
          const isForSelectedChat = selectedChannelRef.current === subscriptionChannel;
          if (!isForSelectedChat) return prev;

          // Only add if not already in list
          const exists = prev.find(m =>
            m.id === messageObj.id ||
            (m.content === messageObj.content &&
              m.sender === messageObj.sender &&
              m.timestamp === messageObj.timestamp)
          );
          if (exists) return prev;
          return [...prev, messageObj];
        });

        // Update chat card with last user message
        updateChatLastMessage(subscriptionChannel, messageObj);
        // Play rec sound if message is not from this agent
        if (messageObj.sender !== username) {
          playSound('rec');
        }
      });

      subscriptionsRef.current[channel] = subscription;

      // Send JOIN only once per channel (tracked by joinedChannelsRef)
      const sender = getSenderForChannel(channel);
      const currentTenantId = chat?.tenantId || tenantId;
      const currentAgentId = chat?.agentId || agentId;

      console.log('Sending JOIN for channel:', channel, 'from sender:', sender);
      stompClient.send('/app/channel.register', {}, JSON.stringify({
        sender: sender,
        tenantId: currentTenantId,
        agentId: currentAgentId,
        channel: channel,
        type: 'JOIN'
      }));
      joinedChannelsRef.current.add(channel);
      console.log('Marked channel as joined:', channel);

    } catch (err) {
      console.error('Failed to subscribe to channel:', err);
    }
  };

  const disconnect = () => {
    // Unsubscribe from all channels
    Object.keys(subscriptionsRef.current).forEach(channel => {
      if (subscriptionsRef.current[channel]) {
        subscriptionsRef.current[channel].unsubscribe();
      }
    });
    subscriptionsRef.current = {};
    joinedChannelsRef.current.clear();

    if (stompClientRef.current && connected) {
      stompClientRef.current.disconnect();
      setConnected(false);
      addSystemMessage('Disconnected from Live Chat');
    }
  };

  // Helper to add message to channel's message log
  const addMessageToChannel = (channel, message) => {
    setMessagesByChannel(prev => {
      const channelMessages = prev[channel] || [];
      const messageWithId = {
        ...message,
        id: message.id || Date.now() + Math.random(),
        timestamp: message.timestamp ? formatTimestamp(message.timestamp) : new Date().toLocaleTimeString()
      };
      return {
        ...prev,
        [channel]: [...channelMessages, messageWithId]
      };
    });
  };

  // Helper to update chat card with last message from user (not agent)
  const updateChatLastMessage = (channel, message) => {
    // Only update if message is from user (not agent/current user)
    if (message.sender === username || message.sender === 'System') {
      return; // Don't update for agent/system messages
    }

    // Mark channel as unread if message is not for the currently selected chat
    if (selectedChannelRef.current !== channel) {
      setUnreadChannels(prev => new Set([...prev, channel]));
    }

    // Update in both openChats and myChats
    setOpenChats(prev => prev.map(chat => {
      if (chat.channel === channel) {
        return {
          ...chat,
          lastMessage: message.content || message.text || 'No messages yet',
          timestamp: formatTimestamp(message.timestamp) || new Date().toLocaleString()
        };
      }
      return chat;
    }));

    setMyChats(prev => prev.map(chat => {
      if (chat.channel === channel) {
        return {
          ...chat,
          lastMessage: message.content || message.text || 'No messages yet',
          timestamp: formatTimestamp(message.timestamp) || new Date().toLocaleString()
        };
      }
      return chat;
    }));
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedChat) return;

    try {
      // Check if connection is active, reconnect if needed
      if (!stompClientRef.current || !stompClientRef.current.connected) {
        console.log('Connection lost, attempting to reconnect before sending...');
        setError('Reconnecting...');
        await initializeConnection();

        // Wait a moment for connection to stabilize
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Double check connection is ready
      if (!stompClientRef.current || !stompClientRef.current.connected) {
        setError('Unable to send message. Connection failed.');
        return;
      }

      const message = {
        content: messageInput,
        sender: username,
        tenantId: tenantId,
        agentId: agentId,
        sessionId: 'session-' + Date.now(),
        type: 'CHAT',
        channel: selectedChat.channel
      };

      // Send via WebSocket - don't add locally, let WebSocket subscription handle it
      stompClientRef.current.send('/app/channel.send', {}, JSON.stringify(message));
      playSound('sent');
      // Clear unread status for this channel when agent sends a message
      setUnreadChannels(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedChat.channel);
        return newSet;
      });

      setMessageInput('');
      setSelectedQuickReply('');
      setError(''); // Clear any errors on successful send
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    }
  };

  const handleQuickReplySelect = (event) => {
    const replyId = event.target.value;
    setSelectedQuickReply(replyId);

    // Find the selected quick reply and populate message input
    if (replyId) {
      const quickReply = quickReplies.find(qr => qr.id === parseInt(replyId));
      if (quickReply) {
        setMessageInput(quickReply.message);
      }
    }
  };

  const handleEndChatClick = () => {
    setEndChatDialogOpen(true);
  };

  const handleEndChatCancel = () => {
    setEndChatDialogOpen(false);
  };

  const handleEndChatConfirm = () => {
    setEndChatDialogOpen(false);
    endChat();
  };

  const handleRequestFormClick = () => {
    if (!connected || !selectedChat) return;

    const formMessage = {
      sender: username,
      tenantId: tenantId,
      agentId: agentId,
      channel: selectedChat.channel,
      type: 'FORM',
      content: `${username} has requested a form.`
    };

    console.log('Sending FORM message for channel:', selectedChat.channel);

    // Send FORM message to STOMP
    stompClientRef.current.send('/app/channel.form', {}, JSON.stringify(formMessage));

    // Add system message locally to show agent action
    addSystemMessage('You have requested a form from the user.', selectedChat.channel);
  };

  const endChat = () => {
    if (!connected || !selectedChat) return;

    const leaveMessage = {
      sender: username,
      tenantId: tenantId,
      agentId: agentId,
      channel: selectedChat.channel,
      type: 'LEAVE',
      content: `${username} has ended the conversation.`
    };

    console.log('Sending LEAVE message for channel:', selectedChat.channel);

    // Send LEAVE message to STOMP
    stompClientRef.current.send('/app/channel.leave', {}, JSON.stringify(leaveMessage));

    // Unsubscribe from this channel
    const subscription = subscriptionsRef.current[selectedChat.channel];
    if (subscription) {
      subscription.unsubscribe();
      delete subscriptionsRef.current[selectedChat.channel];
    }

    // Remove from joinedChannels
    joinedChannelsRef.current.delete(selectedChat.channel);

    // Add system message locally
    addSystemMessage('You have ended the chat.', selectedChat.channel);

    // Move chat from "My" to "All" tab
    const endedChat = { ...selectedChat, status: 'completed', endedAt: new Date() };
    setAllChats(prev => [endedChat, ...prev]);
    setMyChats(prev => prev.filter(chat => chat.channel !== selectedChat.channel));

    // Clear selection
    setSelectedChat(null);
    setActiveMessages([]);
    selectedChannelRef.current = null;

    // Switch to "All" tab to show the completed chat
    setActiveTab(2);
  };

  const pickChat = async (chat) => {
    if (!chat) return;

    setLoading(true);
    setError('');

    try {
      // Call the pickChat API endpoint
      const res = await axios.post(
        `${API_URL}/live/pickChat`,
        {},
        {
          params: {
            channel: chat.channel
          },
          headers: {
            'Authorization': `Bearer ${Cookies.getJwtToken() || localStorage.getItem('token') || 'dummy-token'}`
          }
        }
      );

      console.log('Pick chat response:', res.data);

      // Map ChannelDTO to chat structure (same as fetchUnassignedChats)
      const mappedChats = (res.data || []).map((channelDTO, index) => {
        const channelName = channelDTO.channel || `channel-${index}`;
        const lastMessage = channelDTO.channelMessage?.content || '';
        const timestamp = channelDTO.channelMessage?.dateCreated
          ? new Date(channelDTO.channelMessage.dateCreated).toLocaleString()
          : new Date().toLocaleString();

        // Parse chatHistory if available
        const parsedHistory = parseChatData(channelDTO.chatHistory);

        return {
          id: channelName,
          visitorName: channelDTO.user?.name || 'Visitor',
          lastMessage: lastMessage || 'No messages yet',
          timestamp: timestamp,
          isOnline: channelDTO.online || false,
          channel: channelName,
          history: parsedHistory, // Parsed chat history
          // Keep reference to original DTO for user checking
          hasUser: channelDTO.user != null,
          tenantId: channelDTO.tenantId ? String(channelDTO.tenantId) : '',
          agentId: channelDTO.agentId ? String(channelDTO.agentId) : '',
          agentName: channelDTO.agentName || '',
          liveAssistantName: channelDTO.liveAssistantName || ''
        };
      });

      // Separate unassigned and assigned chats based on user field
      const unassigned = mappedChats.filter(c => !c.hasUser);
      const assigned = mappedChats.filter(c => c.hasUser);

      // Update state with the new lists
      setOpenChats(unassigned);
      setMyChats(assigned.map(c => ({ ...c, pickedAt: new Date() })));

      // Update open chat count
      refreshOpenChatCount(unassigned.length);

      // If this was the last open chat (now 0 open chats), clear the new chat notification
      if (unassigned.length === 0) {
        clearNewChatNotification();
      }

      // Find the picked chat from the assigned chats, or fallback to original chat
      const pickedChat = assigned.find(c => c.channel === chat.channel) || chat;

      // Set identity dynamically from API data
      if (pickedChat) {
        if (pickedChat.liveAssistantName) setUsername(pickedChat.liveAssistantName);
        if (pickedChat.tenantId) setTenantId(String(pickedChat.tenantId));
        if (pickedChat.agentId) setAgentId(String(pickedChat.agentId));
      }

      // Switch to "My" tab (tab index 1)
      setActiveTab(1);

      // Select the picked chat
      setSelectedChat(pickedChat);
      selectedChannelRef.current = pickedChat.channel;
      setChatHistory(pickedChat.history || []);

      // Clear unread status for this channel when picked
      setUnreadChannels(prev => {
        const newSet = new Set(prev);
        newSet.delete(pickedChat.channel);
        return newSet;
      });

      // Load existing messages for this channel from messagesByChannel
      const existingMessages = messagesByChannel[pickedChat.channel] || [];
      setActiveMessages(existingMessages);

      // Subscribe to channel without disconnecting (persistent connection)
      await subscribeToChannel(pickedChat.channel, pickedChat);

    } catch (err) {
      console.error('Failed to pick chat:', err);
      setError('Failed to pick chat. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectChat = async (chat) => {
    setSelectedChat(chat);
    // Set identity dynamically when selecting an assigned chat in "My"
    if (chat) {
      if (chat.liveAssistantName) setUsername(chat.liveAssistantName);
      if (chat.tenantId) setTenantId(String(chat.tenantId));
      if (chat.agentId) setAgentId(String(chat.agentId));
    }
    selectedChannelRef.current = chat.channel;
    setChatHistory(chat.history || []);

    // Clear unread status for this channel when selected
    setUnreadChannels(prev => {
      const newSet = new Set(prev);
      newSet.delete(chat.channel);
      return newSet;
    });

    // Load existing messages for this channel from messagesByChannel
    const existingMessages = messagesByChannel[chat.channel] || [];
    setActiveMessages(existingMessages);

    // Subscribe to channel without disconnecting (persistent connection)
    await subscribeToChannel(chat.channel, chat);
  };

  const addSystemMessage = (text, channel = null) => {
    const systemMessage = {
      id: Date.now() + Math.random(),
      content: text,
      sender: 'System',
      type: 'SYSTEM',
      timestamp: new Date().toLocaleTimeString()
    };

    setActiveMessages(prev => [...prev, systemMessage]);

    // Add to channel log if channel is specified
    if (channel) {
      addMessageToChannel(channel, systemMessage);
    } else if (currentChannelRef.current) {
      addMessageToChannel(currentChannelRef.current, systemMessage);
    }
  };

  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);
    // Fetch user chat logs when All tab is selected
    if (newValue === 2) {
      await Promise.all([fetchAgents(), fetchUserChatLogs()]);
    }
  };

  const getCurrentChatList = () => {
    switch (activeTab) {
      case 0: return openChats;
      case 1: return myChats;
      case 2: return allChats;
      default: return openChats;
    }
  };

  const ChatCard = ({ chat, showPickButton }) => {
    const isUnread = unreadChannels.has(chat.channel);

    return (
      <Card
        sx={{
          mb: 2,
          cursor: 'pointer',
          border: selectedChat?.id === chat.id ? 2 : 0,
          borderColor: primaryColor,
          backgroundColor: isUnread ? '#adadad' : 'white',
          transition: 'background-color 0.3s ease',
          '&:hover': { boxShadow: 3 }
        }}
        onClick={() => !showPickButton && selectChat(chat)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={<OnlineIcon sx={{ fontSize: 12, color: chat.isOnline ? 'success.main' : 'grey.400' }} />}
            >
              <Avatar sx={{ bgcolor: primaryColor }}>
                <PersonIcon />
              </Avatar>
            </Badge>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {chat.visitorName}
                </Typography>
                {chat.channel && (
                  <Chip
                    label={chat.channel}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <TimeIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                <Typography variant="caption" color="text.secondary">
                  {chat.timestamp}
                </Typography>
              </Box>
              {chat.agentName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Agent: {chat.agentName}
                </Typography>
              )}
            </Box>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1,
              fontStyle: chat.lastMessage === 'No messages yet' ? 'italic' : 'normal',
              fontWeight: isUnread ? 'bold' : 'normal',
              color: isUnread ? primaryColor : 'text.secondary',
              animation: isUnread ? 'blink 1.5s ease-in-out infinite' : 'none',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.3 }
              }
            }}
          >
            {chat.lastMessage}
          </Typography>
        </CardContent>
        {showPickButton && (
          <CardActions>
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={() => pickChat(chat)}
              startIcon={<ChatIcon />}
              color="primary"
              sx={{
                backgroundColor: primaryColor,
                '&:hover': { backgroundColor: theme.palette.primary.dark || primaryColor }
              }}
            >
              Pick Chat
            </Button>
          </CardActions>
        )}
      </Card>
    );
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon sx={{ color: primaryColor }} />
            <Typography variant="h5" fontWeight="bold">
              Chats
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={connected ? 'Connected' : 'Disconnected'}
              color={connected ? 'success' : 'default'}
              size="small"
            />
            <Tooltip title="Refresh Chats">
              <IconButton
                size="small"
                onClick={fetchUnassignedChats}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage Quick Replies">
              <IconButton
                size="small"
                onClick={handleOpenPredefinedMessagesDialog}
              >
                <QuickReplyIcon />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Settings">
            <IconButton size="small">
              <SettingsIcon />
            </IconButton>
          </Tooltip> */}
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Three Column Layout */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Column 1: Chat List with Tabs */}
        <Paper elevation={2} sx={{ width: activeTab === 2 ? '15%' : 320, display: 'flex', flexDirection: 'column' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-indicator': {
                backgroundColor: primaryColor
              }
            }}
          >
            <Tab
              label={`Open (${contextOpenChatCount > 0 ? contextOpenChatCount : openChats.length})`}
              sx={{
                '&.Mui-selected': {
                  color: primaryColor
                }
              }}
            />
            <Tab
              label={`My (${myChats.length})`}
              sx={{
                '&.Mui-selected': {
                  color: primaryColor
                }
              }}
            />
            <Tab
              label={`All (${userChatLogs.length})`}
              sx={{
                '&.Mui-selected': {
                  color: primaryColor
                }
              }}
            />
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {activeTab === 2 ? (
              // All Tab: Show user chat logs
              <>
                {loadingAllTab ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : userChatLogs.length === 0 ? (
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No logs found.
                    </Typography>
                  </Box>
                ) : (
                  userChatLogs.map(log => (
                    <Box
                      key={log.id}
                      sx={{ mb: 2, p: 1, borderBottom: '1px solid #eee', cursor: 'pointer', bgcolor: selectedLog && selectedLog.id === log.id ? '#e3f2fd' : 'inherit' }}
                      onClick={() => setSelectedLog(log)}
                    >
                      <Typography variant="body2" sx={{ textAlign: 'left' }}>
                        Agent: {agentMap[log.agentId] || log.agentId}
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: 'left', width: '100%', display: 'block', p: 0, m: 0 }}>{formatDate(log.dateCreated)}</Typography>
                    </Box>
                  ))
                )}
              </>
            ) : (
              getCurrentChatList().map((chat) => (
                <ChatCard
                  key={chat.id}
                  chat={chat}
                  showPickButton={activeTab === 0}
                />
              ))
            )}
          </Box>
        </Paper>

        {/* Column 2: Chat Interface / Chat History */}
        {activeTab === 2 ? (
          // All Tab: Chat History
          <Paper elevation={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ width: '100%', bgcolor: 'primary.main', px: 1, py: 0.5, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', color: '#fff' }}>Chat History</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {loadingAllTab ? (
                <Typography variant="caption">Loading...</Typography>
              ) : selectedLog ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(() => {
                    const parsedChat = parseChatData(selectedLog.chatData);
                    return Array.isArray(parsedChat) && parsedChat.length > 0 ? (
                      parsedChat.map((msg, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-start' : 'flex-end', mb: 1 }}>
                          <Box
                            sx={{
                              bgcolor: msg.role === 'user' ? '#f5f5f5' : primaryColor,
                              color: msg.role === 'user' ? 'text.primary' : 'white',
                              px: 2,
                              py: 1,
                              borderRadius: 2,
                              maxWidth: '70%',
                              boxShadow: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'left', }} dangerouslySetInnerHTML={{ __html: msg.text || msg.content || JSON.stringify(msg) }} />
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">No chat data available.</Typography>
                    );
                  })()}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="body2" color="textSecondary">Select a chat log to view details.</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        ) : (
          // Open/My Tabs: Chat Interface
          <Paper elevation={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" fontWeight="bold">
                    Live Chat - {selectedChat.visitorName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedChat.channel}
                  </Typography>
                </Box>

                {/* Messages Area */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2, backgroundColor: '#fafafa' }}>
                  {connected && (
                    <List>
                      {activeMessages.map((message, index) => (
                        <ListItem
                          key={message.id || index}
                          sx={{
                            flexDirection: 'column',
                            alignItems: message.sender === username ? 'flex-end' : 'flex-start',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: message.sender === username ? primaryColor : 'white',
                              color: message.sender === username ? 'white' : 'text.primary',
                              boxShadow: 1
                            }}
                          >
                            <Typography variant="body2">
                              {message.content}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                              {message.sender} • {formatTimestamp(message.timestamp)}
                            </Typography>
                          </Box>
                        </ListItem>
                      ))}
                      <div ref={messagesEndRef} />
                    </List>
                  )}
                  {!connected && (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        Click "Pick Chat" to start conversation
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Message Input */}
                {connected && (
                  <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    {/* Quick Replies and End Chat Button */}
                    {selectedChat && (
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        {/* Quick Reply Dropdown */}
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel id="quick-reply-label">Quick Replies</InputLabel>
                          <Select
                            labelId="quick-reply-label"
                            id="quick-reply-select"
                            value={selectedQuickReply}
                            label="Quick Replies"
                            onChange={handleQuickReplySelect}
                            startAdornment={<QuickReplyIcon sx={{ mr: 1, ml: 0.5, color: 'action.active' }} />}
                          >
                            <MenuItem value="">
                              <em>Select a message</em>
                            </MenuItem>
                            {quickReplies.map((reply) => (
                              <MenuItem key={reply.id} value={reply.id}>
                                {reply.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Request Form Button */}
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={handleRequestFormClick}
                          startIcon={<FormIcon />}
                        >
                          Request Form
                        </Button>

                        {/* End Chat Button */}
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleEndChatClick}
                          startIcon={<EndChatIcon />}
                        >
                          End Chat
                        </Button>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={3}
                        placeholder="Type your message..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        disabled={loading}
                      />
                      <Button
                        variant="contained"
                        onClick={sendMessage}
                        disabled={!messageInput.trim() || loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                      >
                        Send
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <ChatIcon sx={{ fontSize: 80, color: primaryColor, mb: 2 }} />
                  <Typography variant="h6" sx={{ color: primaryColor }}>
                    Select a chat to view messages
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* Column 3: Chat Log/History or Chat Log Details */}
        {activeTab === 2 ? (
          // All Tab: Chat Log Details
          <Paper elevation={2} sx={{ width: '25%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ width: '100%', bgcolor: 'primary.main', px: 1, py: 0.5, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', color: '#fff' }}>Chat Log Details</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {loadingAllTab ? (
                <Typography variant="caption" sx={{ textAlign: 'left' }}>Loading...</Typography>
              ) : selectedLog ? (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Session ID:</strong> {selectedLog.sessionId}</Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Date Created:</strong> {formatDate(selectedLog.dateCreated)}</Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Date Updated:</strong> {formatDate(selectedLog.dateUpdated)}</Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Input Tokens:</strong> {selectedLog.inputTokens ?? '-'}</Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Output Tokens:</strong> {selectedLog.outputTokens ?? '-'}</Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Chat Rating:</strong> {selectedLog.chatRating ?? '-'}</Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Chat Rating Comments:</strong> {selectedLog.chatRatingComments ?? '-'}</Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Chat Transcript Sent To:</strong> {selectedLog.visitorEmail ?? '-'}</Typography>
                  {/* Always show chatSession details titles, use '-' if not loaded */}
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Internal:</strong> {sessionLogData && sessionLogData.chatSession ? String(sessionLogData.chatSession.internal) : '-'}</Typography>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>Messages Count:</strong> {sessionLogData && sessionLogData.chatSession ? (sessionLogData.chatSession.messagesCount ?? '-') : '-'}</Typography>
                  {/* Always show User Details titles, use '-' if not loaded */}
                  <Box sx={{ width: '100%', bgcolor: 'primary.main', px: 1, py: 0.5, borderRadius: 1, mt: 2, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', color: '#fff' }}>User Details:</Typography>
                  </Box>
                  {(() => {
                    // Parse formData as JSON if possible
                    let userDetails = {};
                    if (sessionLogData && sessionLogData.chatForm && sessionLogData.chatForm.formData) {
                      try {
                        userDetails = JSON.parse(sessionLogData.chatForm.formData);
                      } catch {
                        userDetails = {};
                      }
                    }
                    // If no userDetails, show a default set (or just a dash)
                    const keys = Object.keys(userDetails).length > 0 ? Object.keys(userDetails) : ['Name', 'Email', 'Message', 'Mobile Number'];
                    return keys.map(key => (
                      <Typography key={key} variant="body2" sx={{ mb: 1, textAlign: 'left' }}><strong>{key}:</strong> {userDetails[key] || '-'}</Typography>
                    ));
                  })()}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'left' }}>Select a chat log to view details.</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        ) : (
          // Open/My Tabs: Chat Log/History
          <Paper elevation={2} sx={{ width: 520, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">
                Chat Log
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pre-connection history
              </Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {!chatHistory || chatHistory.length === 0 ? (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No chat history available
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {chatHistory.map((msg, idx) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-start' : 'flex-end', mb: 1 }}>
                      <Box
                        sx={{
                          bgcolor: msg.role === 'user' ? '#f5f5f5' : primaryColor,
                          color: msg.role === 'user' ? 'text.primary' : 'white',
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          maxWidth: '70%',
                          boxShadow: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: msg.text || msg.content || JSON.stringify(msg) }} />
                      </Box>
                    </Box>
                  ))}
                  <div ref={chatHistoryEndRef} />
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* End Chat Confirmation Dialog */}
      <Dialog
        open={endChatDialogOpen}
        onClose={handleEndChatCancel}
        aria-labelledby="end-chat-dialog-title"
        aria-describedby="end-chat-dialog-description"
      >
        <DialogTitle id="end-chat-dialog-title">
          End Chat Conversation?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="end-chat-dialog-description">
            Are you sure you want to end this chat? The user will be notified that you have ended the conversation,
            and you will be disconnected from this channel.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEndChatCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleEndChatConfirm} color="error" variant="contained" autoFocus>
            End Chat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Predefined Messages Management Dialog */}
      <Dialog
        open={predefinedMessagesDialogOpen}
        onClose={handleClosePredefinedMessagesDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: '80%',
            height: '80%',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Manage Quick Replies</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              sx={{ backgroundColor: primaryColor, '&:hover': { backgroundColor: theme.palette.primary.dark || primaryColor } }}
            >
              Create
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          {/* Create/Edit Form */}
          {(isCreating || editingMessage) && (
            <Paper
              ref={formPaperRef}
              elevation={2}
              sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                {editingMessage ? 'Edit Quick Reply' : 'Create New Quick Reply'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Label"
                  fullWidth
                  value={formLabel}
                  onChange={(e) => setFormLabel(e.target.value)}
                  placeholder="Enter label (e.g., Greeting, Thank You)"
                  required
                />
                <TextField
                  inputRef={messageTextFieldRef}
                  label="Message"
                  fullWidth
                  multiline
                  rows={3}
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  placeholder="Enter message text"
                  required
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={handleCancelForm}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitForm}
                    disabled={!formLabel.trim() || !formMessage.trim()}
                    sx={{ backgroundColor: primaryColor, '&:hover': { backgroundColor: theme.palette.primary.dark || primaryColor } }}
                  >
                    {editingMessage ? 'Update' : 'Submit'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* List of Quick Replies */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Quick Replies List
          </Typography>
          {loadingMessages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : quickReplies.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No quick replies found. Click "Create" to add a new one.
              </Typography>
            </Box>
          ) : (
            <List>
              {quickReplies.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: editingMessage?.id === message.id ? '#e3f2fd' : 'white',
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                >
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {message.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {message.message}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(message)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(message)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePredefinedMessagesDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Quick Reply?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{messageToDelete?.label}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveChat;